import { useTranslation } from 'react-i18next';
import { UserRound } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

/**
 * "Signed in as … — not you?", after the app has been away for hours (DEBT-7).
 *
 * The one moment worth interrupting on a shared handset is the moment it changes hands,
 * and that moment is indistinguishable from any other resume — except that it follows a
 * long absence. This rides the `visibilitychange` listener D40 already added for session
 * revalidation, so it costs no new listener and no schema.
 *
 * It asks rather than acts. An idle timeout was considered and refused (D40, D8): the
 * housekeeper who is the sole user of her phone must not be signed out mid-report in a
 * basement, and she is the common case. A prompt she dismisses in one tap costs her a
 * tap; a timeout costs her the report.
 *
 * Sign out is offered first because it is the point of the prompt — the person reading
 * this and answering "no" wants their own account, not this one.
 */
export function IdentityCheck() {
  const { t } = useTranslation();
  const { user, identityUnconfirmed, confirmIdentity, signOut } = useAuth();

  if (!identityUnconfirmed || !user) return null;

  return (
    <div
      role="alert"
      className="border-b border-brookfields-gold/40 bg-slate-900 px-4 py-3 text-sm text-slate-100"
    >
      <p className="flex items-center gap-2 font-semibold">
        <UserRound className="size-4 shrink-0" aria-hidden="true" />
        {/* The email, not a name: the session payload carries no display name, and this
            has to match the identity already shown in the header above it. */}
        <span className="truncate">{t('session.prompt', { email: user.email })}</span>
      </p>
      <p className="mt-1 text-xs text-slate-400">{t('session.question')}</p>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => void signOut()}
          className="min-h-[48px] flex-1 rounded-lg bg-brookfields-green px-4 text-sm font-semibold text-white ring-1 ring-brookfields-gold/70"
        >
          {t('session.signOut')}
        </button>
        <button
          type="button"
          onClick={confirmIdentity}
          className="min-h-[48px] shrink-0 rounded-lg border border-slate-700 bg-transparent px-4 text-sm text-slate-300"
        >
          {t('session.confirm')}
        </button>
      </div>
    </div>
  );
}
